<template>
  <page-outline 
    :title="$t('Webheader.usercompany')"
    :show-filter="true"
    :show-refresh="true"
    :show-extract="true"
    :filter-expanded="displayFilter"
    :refresh-disabled="activateSpinner"
    :extract-disabled="activateSpinner"
    @toggle-filter="toggleFilter"
    @refresh="refreshList"
    @extract="exportData">
    <!-- Primary Actions -->
    <template slot="primaryActions">
      <app-button
        :text="$t('administration.user_add')"
        icon="fa-light fa-plus"
        variant="primary"
        size="md"
        :disabled="!canAddUser"
        :aria-label="$t('administration.user_add')"
        @click="addUser"
      />
    </template>
    
    <!-- Section de filtres -->
    <template slot="filterContent">
      <app-panel
        :filters="filterConfig"
        :searchAriaLabel="$t('common.srch') + ' ' + $t('Webheader.usercompany')"
        @filter-changed="onFilterChanged"
        @clear-filters="clearAllFilters"
        @apply-filters="applyFilter"
      />
    </template>
    
    <template slot="pageContent">
      <b-container fluid>
        <div>
          <section aria-label="Liste des utilisateurs">
            <h2 class="sr-only">Liste des utilisateurs</h2>
            <b-row>
              <b-col>
                <div v-if="activateSpinner" role="status" aria-live="polite" :aria-label="$t('common.loading')">
                  <spinner></spinner>
                  <span class="sr-only">{{ $t('common.loading') }}</span>
                </div>
                <b-table responsive hover v-else-if="UsersList.length > 0"
                         class="table table-striped"
                         :fields="fields"
                         :items="UsersList"
                         :selectable="UsersList.length > 0"
                         :empty-text="$t('common.security.no_result')"
                         :sort-by.sync="sortBy"
                         :sort-desc.sync="sortDir"
                         @head-clicked="sortTable"
                         @row-selected="seeUserDetails">
                  <template #cell(Active)="data">
                    <div class="d-flex align-items-center justify-content-center">
                      <i v-if="data.value === '0'" class="fa-light fa-xmark btn btn-icon btn-icon-red" aria-hidden="true"></i>
                      <i v-if="data.value === '2'" class="fa-light fa-check btn btn-icon btn-icon-green" aria-hidden="true"></i>
                      <i v-if="data.value === '1'" class="fa-light fa-warning btn btn-icon btn-icon-orange" aria-hidden="true"></i>
                      <span class="sr-only">
                        {{ data.value === '0' ? $t('administration.no') : data.value === '2' ? $t('administration.yes') : $t('administration.in_prgrss') }}
                      </span>
                    </div>
                  </template>
                  <template #cell(Account)="data">
                    {{data.item.Rights[0].AccountName}}
                  </template>
                  <template #cell(delete)="data">
                    <span class="text-center d-inline-block py-4 w-100" @click="activateDelete($event, data.value)"><i class="fa-light fa-trash"></i></span>
                    <span>{{ data.value.IdUser }}</span>
                  </template>
                </b-table>
                <div v-else class="d-flex justify-content-center" role="status" aria-live="polite">
                  <span class="noResult">
                    {{ $t('common.no_result') }}
                  </span>
                </div>
              </b-col>
            </b-row>
            
            <!-- Zone de messages aria-live pour les changements dynamiques -->
            <div aria-live="polite" aria-atomic="true" class="sr-only" id="status-messages">
              {{ statusMessage }}
            </div>
            <b-row class="mt-5">
              <b-col>
                <cm-pagination
                  :maxResultsByPage="paginationObject.maxResults"
                  :firstIndex="paginationObject.firstIndex"
                  :nbResultsTotal="paginationObject.nbResultsTotal"
                  :refreshEvent="paginationObject.refresh">
                </cm-pagination>
              </b-col>
            </b-row>
          </section>
        </div>
      </b-container>
    </template>
  </page-outline>
</template>

<script>
import moment from 'moment-timezone'
import Pagination from '@/components/common/pagination.vue'
import Spinner from '@/components/common/spinner.vue'
import {errorMessageAlert} from '@/helpers/alert'
import {loadPage} from '@/helpers/routing'
import PageOutline from '@/components/common/PageOutline.vue'
import AppButton from '@/components/common/AppButton.vue'
import AppPanel from '@/components/common/AppPanel.vue'

export default {
  name: 'Users',
  components: {
    'cm-pagination': Pagination,
    'spinner': Spinner,
    'page-outline': PageOutline,
    AppButton,
    AppPanel
  },
  props: {
    additionalClass: {
      type: String,
      default: ''
    },
    direction: {
      default: '2',
      type: String
    }
  },
  computed: {
    allCompanies () {
      return this.profiles.map(profile => {
          return {
            name: profile.name
          }
        })
    },
    filterConfig() {
      return [
        {
          id: 'filter-identifier',
          type: 'text',
          prop: 'login',
          labelKey: 'administration.identifier',
          icon: 'fa-solid fa-user',
          value: this.login,
          autocomplete: 'username',
          colSm: '6',
          colLg: '4'
        },
        {
          id: 'filter-company',
          type: 'select',
          prop: 'nameCompany',
          labelKey: 'administration.company_name',
          icon: 'fa-solid fa-building',
          value: this.nameCompany,
          autocomplete: 'organization',
          defaultOptionKey: 'administration.select_company',
          colSm: '6',
          colLg: '4',
          options: this.allCompanies.map(company => ({
            value: company.name,
            label: company.name
          }))
        },
        {
          id: 'filter-active',
          type: 'select',
          prop: 'userActive',
          labelKey: 'administration.active',
          icon: 'fa-solid fa-circle-check',
          value: this.userActive,
          defaultOptionKey: 'administration.select_activity',
          colSm: '6',
          colLg: '4',
          options: [
            { value: '0', labelKey: 'administration.no' },
            { value: '1', labelKey: 'administration.in_prgrss' },
            { value: '2', labelKey: 'administration.yes' }
          ]
        }
      ]
    },
    buttonsListStyle () {
      return (!this.isSmartphoneOS) ? 'border: 1px solid #aaa;' : ''
    },
    canUserDelete () {
      return this.$engine.firstOn().rights.rightIs('usercompany_delete', true) || this.$engine.firstOn().rights.rightIs('usercompany_delete_admin', true)
    },
    fields () {
      let fields = [
        { key: 'Name', label: this.$i18n.t('administration.user_name'), class: 'text-center', sortable: true },
        { key: 'Login', label: this.$i18n.t('administration.identifier'), class: 'text-center', sortable: true },
        { key: 'Account', label: this.$i18n.t('administration.company_name'), class: 'text-center', sortable: false },
        { key: 'Active', label: this.$i18n.t('administration.active'), class: 'text-center', sortable: false },
      ]

      if (this.canUserDelete) {
        fields.push({ key: 'delete', label: this.$i18n.t('administration.del'), thClass: 'text-center table-condensed-ipad col-1', tdClass: 'p-0' })
      }

      return fields
    },
    isSmartphoneOS () {
      return this.$f7.device.os === 'android' || this.$f7.device.os === 'ios'
    },
    company_nameDirection () {
      return ((this.sortField === 'company_name' && this.sortDirection === 'ASC') ? 'fa-caret-up' : 'fa-caret-down')
    },
    loginDirection () {
      return ((this.sortField === 'login' && this.sortDirection === 'ASC') ? 'fa-caret-up' : 'fa-caret-down')
    },
    activeDirection () {
      return ((this.sortField === 'active' && this.sortDirection === 'ASC') ? 'fa-caret-up' : 'fa-caret-down')
    },
    hasInputFilter () {
      if (
        this.nameCompany ||
        this.login ||
        this.userActive) {
        return true
      }
      return false
    }
  },
  data () {
    return {
      activateSpinner: false,
      timezone: '+00:00',
      canAddUser: false,
      displayFilter: false,
      filterBy: '',
      filterConditions: {},
      hasDefaultFilter: false,
      idUserAcc: '',
      login: '',
      mode: 1,
      nameCompany: '',
      noMore: false,
      pageCounter: 0,
      paginationObject: {},
      profiles: [],
      randomId: Math.random().toString(36).substr(2, 10),
      requestsNumber: '',
      scrollFlag: true,
      sortDirection: 'DESC',
      sortField: 'dateAction',
      sortBy: '',
      sortDir: false,
      userActive: '',
      UsersList: [],
      statusMessage: ''
    }
  },
  created () {
    this.timezone = moment.tz.guess()
    
    if (this.filterConditions) {
      if (this.filterConditions.OrderByField) {
        this.sortBy = this.filterConditions.OrderByField
      }
      if (this.filterConditions.OrderByDirection) {
        this.sortDir = this.filterConditions.OrderByDirection === 'DESC'
      }
    }

    this.$bus.$emit('switch-selected-menu', '/usercompany/')
    this.$bus.$emit('change-mobile-header', {
      backLink: false,
      navbarInnerStyle: 'justify-content:start;',
      navTitle: 'Utilisateurs',
    })
    this.paginationObject['firstIndex'] = 1
    this.paginationObject['maxResults'] = 13
    this.paginationObject['nbResultsTotal'] = 0
    let that = this
    this.$bus.$off('refresh-cache-contacts')

    this.paginationObject['refresh'] = 'direct-inbox-filtered'
    this.$bus.$off('direct-inbox-filtered')
    this.$bus.$on('direct-inbox-filtered', index => {
      this.onRefresh(false, index)
      that.pageCounter = 0
    })

    this.$f7.initPullToRefresh()
    this.pageCounter = 0
    this.onRefresh(false)
    this.filterTypeGet = this.filterType
    this.$bus.emit('right-panel-set-feature', 'filter-messages')
    this.$engine.firstOn().company.profiles.forEach(profile => {
      let existingProfile = that.profiles.find(obj => obj.idAccount === profile.idAccount)
      if (!existingProfile) {
        that.profiles.push({
          idAccount: profile.idAccount,
          name: profile.nameAccount,
          profiles: [{
            id: profile.id_rights_profile,
            name: profile.libelle
          }]
        })
      } else if (!existingProfile.profiles.some(obj => obj.id === profile.id_rights_profile)) {
        existingProfile.profiles.push({
          id: profile.id_rights_profile,
          name: profile.libelle
        })
      }
    })
  },
  mounted () {
    this.$$('.infinite-scroll-preloader').remove()
    let defaultFilter = this.$engine.firstOn().usercompany.getDefaultFilter()
    this.setDefaultFilter(defaultFilter)
  },
  beforeDestroy () {
    let that = this
    that.$engine.firstOn().usercompany.cleanFilterCondition()
  },
  methods: {
    addUser () {
      let that = this
      loadPage(that, {url: '/addusercompany/undefined'})
    },
    applyFilter (filterConditions = {}) {
      this.filterConditions = filterConditions

      let that = this
      let defaultFilter = this.$engine.firstOn().usercompany.getDefaultFilter()
      this.filterTypeGet = 'list'
      this.setDefaultFilter(defaultFilter)
      this.$engine.firstOn().usercompany.cleanFilterCondition()
      this.$engine.firstOn().usercompany.userActive = this.userActive
      this.$engine.firstOn().usercompany.nameCompany = this.nameCompany.trim()
      this.$engine.firstOn().usercompany.login = this.login.trim()

      for (let propertyCustom in filterConditions) {
        this.$engine.firstOn().usercompany[propertyCustom] = filterConditions[propertyCustom]
      }
      filterConditions = this.$engine.firstOn().usercompany.getConditionObject()
      // this.$emit('applyFilter', actualFilter.getConditionObject())

      this.pageCounter = 0
      this.paginationObject['firstIndex'] = 1
      this.paginationObject['nbResultsTotal'] = 0

      filterConditions.sortField = 'SF' + this.sortField
      filterConditions.sortDirection = this.sortDirection
      this.activateSpinner = true

      if (this.filterConditions.OrderByField) {
        this.sortBy = this.filterConditions.OrderByField
        filterConditions.OrderByField = this.filterConditions.OrderByField
      }
      if (this.filterConditions.OrderByDirection) {
        this.sortDir = this.filterConditions.OrderByDirection === 'DESC'
        filterConditions.OrderByDirection = this.filterConditions.OrderByDirection
      }

      this.$engine.firstOn().usercompany.getUsers(
        this.paginationObject.maxResults,
        this.paginationObject.firstIndex,
        filterConditions
      ).then(resp => {
        let requests = {}
        that.paginationObject.nbResultsTotal = resp.data.nbTotalResults
        that.paginationObject.nbResults = resp.data.users.length
        resp.data.users.forEach(item => {
          item.company_name = item.company_name
          item.login = item.login
          item.active = item.active
        })
        requests = resp.data.users
        that.updateInbox(requests)
        // this.$bus.emit('refresh-cache-paging-template', this.paginationObject)
        that.activateSpinner = false
      })
      .catch(error => {
        errorMessageAlert(error, that)
      })
    },
    toggleRightPanel () {
      this.$bus.emit('right-panel-toggle')
    },
    communicateChange () {
      this.$bus.emit('right-panel-filter-view-update-fields', this.filterFields)
    },
    onFilterChanged({ prop, value }) {
      this[prop] = value
    },
    clearAllFilters() {
      this.clearFilterFields()
    },
    clearFilterFields () {
      this.nameCompany = ''
      this.login = ''
      this.userActive = ''
      this.communicateChange()
      // this.applyFilter()
    },
    deleteFilter () {
      this.$engine.firstOn().usercompany.saveDefaultFilter({})
      this.hasDefaultFilter = false
      this.$f7.modal({
        title: this.$i18n.t('administration.del_filter'),
        buttons: [
          {
            text: 'OK',
            onClick: function () {
              return
            }
          }
        ]
      })
    },
    getCompanyName (infos) {
      return infos.Rights && infos.Rights[0] && infos.Rights[0].AccountName ? infos.Rights[0].AccountName : ''
    },
    saveDefaultFilter () {
      let that = this

      let defaultFilter = {
        nameCompany: this.nameCompany.trim(),
        login: this.login.trim(),
        userActive: this.userActive
      }

      this.$engine.firstOn().usercompany.saveDefaultFilter(defaultFilter)

      if (this.hasInputFilter) {
        this.hasDefaultFilter = true
      }

      that.$f7.modal({
        title: this.$i18n.t('filter.dflt_fltr_svd'),
        buttons: [
          {
            text: 'OK',
            onClick: function () {
              return
            }
          }
        ]
      })
    },
    setDefaultFilter (defaultFilter) {
      for (const property in defaultFilter) {
        this[property] = defaultFilter[property]
        if (defaultFilter[property]) {
          this.hasDefaultFilter = true
        }
      }
    },
    attachInfinit () {
      if (this.$settings.inboxPreloaderRemoved ||
          this.$settings.sendboxPreloaderRemoved) {
        this.$f7.attachInfiniteScroll(this.$$('.infinite-scroll'))
        this.$engine.firstOn().settings.set({inboxPreloaderRemoved: false})
      }
      this.scrollFlag = true
      this.noMore = false
    },
    detachInfinit () {
      this.$$('.infinite-scroll-preloader').remove()
      this.$f7.detachInfiniteScroll(this.$$('.infinite-scroll'))
      this.$engine.firstOn().settings.set({inboxPreloaderRemoved: true})
      if (!this.emptyBox) {
        this.scrollFlag = false
        this.noMore = true
      }
    },
    exportUserCompany () {
      this.$f7.showPreloader(this.$i18n.t('common.loading_page_label'))
      let firstLine = this.$t('administration.company_name') + ';' +
        this.$t('administration.identifier') + ';' +
        this.$t('administration.active') + '\r\n'
      let lines = ''
      for (var i = 0; i < this.UsersList.length; i++) {
        let result = this.UsersList[i]
        let iconActive
        if (result.Active === "0") {
          iconActive = this.$t('administration.no')
        } else if (result.Active === "1") {
          iconActive = this.$t('administration.in_prgrss')
        } else if (result.Active === "2") {
          iconActive = this.$t('administration.yes')
        }
        lines += result.Rights[0].AccountName + ';' +
                 result.Login + ';' +
                 iconActive + '\r\n'
      }
      let data = firstLine + lines
      this.$f7.hidePreloader()
      let linkSource = 'data:text/csv;charset:utf-8;base64,' + btoa(data)
      let downloadLink = document.createElement('a')
      let today = moment()
      let fileName = 'usercompany_' + today.unix() + '.csv'
      if (
      this.$f7.device.os === 'android' ||
      this.$f7.device.os === 'ios'
      ) {
        downloadFileWithDefinedHeaders(this, fileName, linkSource)
      } else {
      downloadLink.href = linkSource
      downloadLink.download = fileName
      downloadLink.click()
      }
      this.$f7.alert('', this.$i18n.t('common.exprt_succ'))
    },
    toggleFilter () {
      this.displayFilter = !this.displayFilter
    },
    refreshList () {
      this.onRefresh()
    },
    exportData () {
      this.exportUserCompany()
    },
    onClear () {
      this.filterBy = ''
      this.attachInfinit()
    },
    onDisable () {
      this.attachInfinit()
    },
    onEnable () {
      this.detachInfinit()
    },
    onInfinite () {
      if (this) {
        return
      }
      if (this.scrollFlag) {
        this.scrollFlag = false
        let that = this
        this.pageCounter = this.pageCounter + 1
        this.paginationObject['firstIndex'] = this.pageCounter * this.paginationObject['maxResults'] + 1
        this.paginationObject['nbResultsTotal'] = 0

        let filterConditions
        if (this.$engine.firstOn().usercompany.getDefaultFilter()) {
          filterConditions = this.$engine.firstOn().usercompany.getDefaultFilter()
        } else {
          filterConditions = this.$engine.firstOn().usercompany.getConditionObject()
        }
        filterConditions.sortField = 'SF' + this.sortField
        filterConditions.sortDirection = this.sortDirection
        this.$engine.firstOn().usercompany.getUsers(
          this.paginationObject['maxResults'],
          this.paginationObject['firstIndex'],
          filterConditions
        ).then(resp => {
          let requests = {}
          that.paginationObject.nbResultsTotal = resp.data.nbTotalResults
          that.requestsNumber = resp.data.users.length
          resp.data.users.forEach(item => {
            item.login = item.login
            item.company_name = item.company_name
            item.active = item.active
          })
          requests = resp.data.users
          that.paginationObject.nbResults = that.requestsNumber

          if (requests.length > 0) {
            for (let i = 0; i < requests.length; i++) {
              that.UsersList.push(requests[i])
            }
            if (requests.length < this.paginationObject['maxResults']) {
              that.detachInfinit()
            }
          } else {
            that.detachInfinit()
          }
          that.scrollFlag = true
        })
        .catch(error => {
          errorMessageAlert(error, that, that.$i18n)
          that.scrollFlag = true
        })
      }
    },
    onRefresh (noAttach = false, index = 1) {
      let that = this
      this.pageCounter = 0
      this.paginationObject['firstIndex'] = index
      this.paginationObject['nbResultsTotal'] = 0

      this.activateSpinner = true
      let filterConditions
      if (this.$engine.firstOn().usercompany.getDefaultFilter()) {
        filterConditions = this.$engine.firstOn().usercompany.getDefaultFilter()
      } else {
        filterConditions = this.$engine.firstOn().usercompany.getConditionObject()
      }
      filterConditions.sortField = 'SF' + this.sortField
      filterConditions.sortDirection = this.sortDirection

      this.$engine.firstOn().usercompany.cleanFilterCondition()
      this.$engine.firstOn().usercompany.userActive = this.userActive
      this.$engine.firstOn().usercompany.nameCompany = this.nameCompany.trim()
      this.$engine.firstOn().usercompany.login = this.login.trim()

      for (let propertyCustom in filterConditions) {
        this.$engine.firstOn().usercompany[propertyCustom] = filterConditions[propertyCustom]
      }
      filterConditions = this.$engine.firstOn().usercompany.getConditionObject()

      if (this.filterConditions.OrderByField) {
        filterConditions.OrderByField = this.filterConditions.OrderByField
      }
      if (this.filterConditions.OrderByDirection) {
        filterConditions.OrderByDirection = this.filterConditions.OrderByDirection
      }

      this.$engine.firstOn().usercompany.getUsers(
        this.paginationObject.maxResults,
        this.paginationObject.firstIndex,
        filterConditions
      ).then(resp => {
        let requests = {}
        if (resp.data && resp.data.users.length > 0) {
          that.paginationObject.nbResultsTotal = resp.data.nbTotalResults
          that.paginationObject.nbResults = resp.data.users.length
          resp.data.users.forEach(item => {
          //  item.id_user_account = item.id_user_account
          //  item.login = item.login
          //  item.id_account = item.id_account
            item.delete = item.IdUser
          })
          requests = resp.data.users
          that.updateInbox(requests, noAttach)
          that.$f7.pullToRefreshDone()
          that.$bus.emit('refresh-cache-paging-template', that.paginationObject)
        }
        if (!that.canAddUser) {
          that.$engine.firstOn().rights.getAllProfilesLight()
          .then(resp => {
            that.$engine.firstOn().company.profiles = resp.profiles.map(profile => {
              return {
                id_rights_profile: profile.idProfile,
                libelle: profile.libelleProfile,
                idAccount: profile.idAccount,
                nameAccount: profile.accountName
              }
            })
          })
          .catch(err => {
            return err
          })
          .finally(() => {
            that.canAddUser = true
            that.activateSpinner = false
          })
        } else {
          that.activateSpinner = false
        }
      })
      .catch(error => {
        that.activateSpinner = false
        errorMessageAlert(error, that)
      })
    },
    onSearch (query, found) {
      this.filterBy = query
    },
    refreshInbox () {
      this.onRefresh()
    },
    activateDetail (ressource, id) {
      let that = this
      loadPage(that, {url: '/detailusercompany/' + ressource + '/' + id})
    },
    activateEdit (ressource, id) {
      let that = this
      loadPage(that, {url: '/editusercompany/' + ressource + '/' + id})
    },
    activateDelete ($event, idUserToDelete) {
      $event.stopPropagation()
      let that = this
      this.$f7.modal({
        title: this.$i18n.t('administration.del_user_qt'),
        buttons: [
          {
            text: this.$t('creation_forms.vldt'),
            onClick: function () {
              that.$engine.firstOn().usercompany.deleteUser(idUserToDelete)
              .then(resp => {
                that.$f7.alert('', that.$t('administration.del_user'))
                that.onRefresh(false)
              })
            }
          },
          {
            text: this.$t('common.cncl'),
            onClick: function () {
              return
            }
          }
        ]
      })
    },
    reOrderBy (column) {
      let that = this
      if (this.sortField === column) {
        this.sortDirection = this.sortDirection === 'ASC' ? 'DESC' : 'ASC'
      } else {
        this.sortField = column
        switch (column) {
          case 'dateAction':
            this.sortDirection = 'DESC'
        }
      }
      let filterConditions
      if (this.$engine.firstOn().usercompany.getDefaultFilter()) {
        filterConditions = this.$engine.firstOn().usercompany.getDefaultFilter()
      } else {
        filterConditions = this.$engine.firstOn().usercompany.getConditionObject()
      }
      filterConditions.sortField = 'SF' + this.sortField
      filterConditions.sortDirection = this.sortDirection
      this.$engine.firstOn().usercompany.getUsers(
        this.paginationObject.maxResults,
        this.paginationObject.firstIndex,
        filterConditions
      ).then(resp => {
        let requests = {}
        that.paginationObject.nbResults = resp.data.users.length
        resp.data.users.forEach(item => {
          item.login = item.login
          item.company_name = item.company_name
          item.active = item.active
        })
        requests = resp.data.users
        that.updateInbox(requests, false)
        that.$f7.pullToRefreshDone()
        that.$bus.emit('refresh-cache-paging-template', that.paginationObject)
        that.activateSpinner = false
      })
      .catch(error => {
        that.activateSpinner = false
        errorMessageAlert(error, that)
      })
    },
    updateInbox (requests, noAttach = false) {
      this.UsersList = requests
      if (requests.length === this.paginationObject['maxResults']) {
        if (!noAttach) {
          this.attachInfinit()
        }
      } else {
        this.detachInfinit()
      }
    },
    seeUserDetails (items) {
      this.activateDetail(items[0].IdUser, items[0].Rights[0].IdAccount)
    },
    sortTable (key, field, event, isFooter) {
      const sortableFields = ['Name', 'Login']

      if (!sortableFields.includes(key)) {
        return
      }

      let sortDirection = 'ASC'
      if (key === this.sortBy && this.sortDir === false) {
        sortDirection = 'DESC'
      }

      this.filterConditions.OrderByField = key
      this.filterConditions.OrderByDirection = sortDirection

      this.applyFilter(this.filterConditions, this.paginationObject.firstIndex)
    },
    showButtonImportLot () {
      return false
    }
  }
}
</script>
